import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarTrigger,
  cn,
  useSidebar,
} from '@parcel/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import {
  ArrowLeft,
  Blocks,
  BookOpen,
  Building2,
  Check,
  ChevronRight,
  HardHat,
  Layers,
  LogOut,
  Newspaper,
  Plus,
  Settings,
  Sparkles,
  Tag,
  Users,
} from 'lucide-react';
import * as React from 'react';
import { useAuth } from '../lib/auth.js';
import { activeFavoriteViewId } from '../lib/favorite-views-state.js';
import { useFeedUnseenCount } from '../lib/feed.js';
import { isSpecSurfaceVisible } from '../lib/spec-config.js';
import { readSpecChatId } from '../lib/spec-conversations.js';
import { useSpecLaunchState } from '../lib/spec-launch.js';
import {
  type WorkspaceView,
  useFavoriteViewMutations,
  useFavoriteViews,
} from '../lib/workspace-views.js';
import {
  type WorkspaceCapabilities,
  type WorkspaceSummary,
  getWorkspace,
  listWorkspaces,
} from '../lib/workspaces.js';
import {
  type SettingsNavItem,
  filterSettingsNavGroups,
  getSidebarMode,
  getSidebarWorkspaceInitial,
  getSwitchableWorkspaces,
  getWorkspaceScopedPath,
  getWorkspaceSlugFromPathname,
  getWorkspaceSwitchPath,
  isSidebarNavItemActive,
  settingsNav,
} from './app-sidebar-nav.js';
import { AuthenticatedImage } from './auth/authenticated-image.js';
import { FavoriteViewsSection } from './favorite-views-section.js';
import { getWorkspaceDefaults } from './settings/workspace-settings.js';
import { SidebarCredits } from './sidebar-credits.js';
import { SpecSidebarChatsSkeleton } from './spec/sidebar-chats-skeleton.js';

const research = [
  { label: 'Accounts', to: '/accounts', icon: Building2 },
  { label: 'Contacts', to: '/contacts', icon: Users },
  { label: 'Projects', to: '/projects', icon: Tag },
] as const;

// Exported so app-sidebar.test.ts can assert order and Spec gating without rendering the sidebar.
export const home = [
  { label: 'News feed', to: '/feed', icon: Newspaper },
  { label: 'Spec', to: '/spec', icon: HardHat, specOnly: true },
  { label: 'Knowledge', to: '/knowledge', icon: BookOpen, specOnly: true },
  { label: 'Skills', to: '/skills', icon: Layers, specOnly: true },
  { label: 'Connectors', to: '/connectors', icon: Blocks, specOnly: true },
] as const;

/**
 * Chat history, loaded on demand.
 *
 * Lazy for the same reason the runtime itself is: this component reads the Spec
 * runtime context, and a static import here would pull `@assistant-ui/react`
 * into the entry chunk through the always-mounted sidebar. Rendered only when
 * the Spec surface is visible, so production never fetches it.
 */
const LazySpecSidebarChats = React.lazy(() =>
  import('./spec/sidebar-chats.js').then((module) => ({ default: module.SpecSidebarChats })),
);

/**
 * Marks Spec as the AI surface, in the column the Notifications badge occupies.
 *
 * The svg is wrapped rather than placed directly in SidebarMenuButton, whose own
 * `[&>svg]:size-4` rule would otherwise win on specificity and resize it. Colour is the
 * `brand` token, never a literal green: `--brand` is declared once per mode in
 * packages/ui/src/styles/globals.css and is lighter in dark for contrast.
 */
export function SpecAiMark() {
  return (
    <span className="ml-auto inline-flex items-center text-brand" data-testid="spec-ai-mark">
      <Sparkles className="size-3.5" aria-hidden />
      {/* Not decorative: the icon is the only thing saying this surface is an assistant. */}
      <span className="sr-only">AI</span>
    </span>
  );
}

export const sidebarCollapsedTriggerTextClassName = 'group-data-[collapsible=icon]:hidden';
export const sidebarBackToAppLabel = 'Back to app';

export function getSidebarWorkspaceMenuLabel(workspaceName: string) {
  return `Open workspace menu for ${workspaceName}`;
}

export function AppSidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const chatOpen = useRouterState({ select: (s) => readSpecChatId(s.location.search) !== null });
  const urlViewId = useRouterState({
    select: (s) => {
      const view = (s.location.search as { view?: unknown }).view;
      return typeof view === 'string' ? view : null;
    },
  });
  const { session, signOut } = useAuth();
  const sidebarMode = getSidebarMode(pathname);
  const activeWorkspaceSlug = getWorkspaceSlugFromPathname(pathname);
  const activeWorkspaceQuery = useQuery({
    queryKey: ['workspace', activeWorkspaceSlug],
    enabled: Boolean(activeWorkspaceSlug),
    queryFn: () => getWorkspace(activeWorkspaceSlug ?? ''),
  });
  const activeWorkspace = activeWorkspaceQuery.data ?? null;
  const workspacesQuery = useQuery({ queryKey: ['workspaces'], queryFn: listWorkspaces });
  const userId = session?.user.id;
  const linkFor = (to: `/${string}`) => getWorkspaceScopedPath(pathname, to);
  const feedUnseenCount = useFeedUnseenCount(activeWorkspace?.id);
  const sidebarLaunchState = useSpecLaunchState();
  const showCredits = sidebarMode !== 'settings' && isSpecSurfaceVisible(sidebarLaunchState);
  const favoritesQuery = useFavoriteViews(activeWorkspace?.id, userId);
  const favoriteMutations = useFavoriteViewMutations(activeWorkspace?.id, userId);

  const activeFavoriteId = activeFavoriteViewId(
    favoritesQuery.data ?? [],
    activeWorkspaceSlug,
    pathname,
    urlViewId,
  );

  // A View owns the highlight while it is open. Without this the row lights up and so does
  // Accounts above it, and the sidebar claims you are in two places at once.
  const isActive = (to: string) =>
    !activeFavoriteId && isSidebarNavItemActive({ to, pathname, chatOpen });

  return (
    <Sidebar collapsible="icon" className="group/sidebar border-r-0">
      {sidebarMode === 'settings' ? (
        <SettingsSidebarHeader appHomePath={linkFor('/feed')} />
      ) : (
        <AppSidebarHeader
          workspace={activeWorkspace}
          workspaces={getSwitchableWorkspaces(workspacesQuery.data, activeWorkspace)}
          settingsGeneralPath={linkFor('/settings/general')}
          settingsMembersPath={linkFor('/settings/members')}
          onSignOut={signOut}
        />
      )}

      <SidebarContent className={cn('gap-0', showCredits && 'sidebar-content-fade')}>
        {sidebarMode === 'settings' ? (
          <SettingsSidebarContent
            isActive={isActive}
            linkFor={linkFor}
            capabilities={activeWorkspace?.capabilities ?? null}
          />
        ) : (
          <AppSidebarContent
            isActive={isActive}
            linkFor={linkFor}
            homeBadgeCount={feedUnseenCount}
            favorites={favoritesQuery.data ?? []}
            activeFavoriteId={activeFavoriteId}
            workspaceSlug={activeWorkspaceSlug}
            userId={userId}
            workspaceId={activeWorkspace?.id}
            onUnfavorite={(viewId) => favoriteMutations.unfavorite.mutate(viewId)}
            onReorder={(viewIds) => favoriteMutations.reorder.mutate(viewIds)}
          />
        )}
      </SidebarContent>

      {/* Credits are a Spec concept, so the row keeps the same gate the Spec nav items do. */}
      {!showCredits ? null : (
        <SidebarFooter className="p-2">
          <SidebarCredits
            workspaceId={activeWorkspace?.id}
            usageHref={linkFor('/settings/usage')}
            className="mx-4 group-data-[collapsible=icon]:mx-0"
          />
        </SidebarFooter>
      )}

      <SidebarRail />
    </Sidebar>
  );
}

/**
 * Compact workspace switcher + in-sidebar collapse control (shadcn SidebarTrigger).
 *
 * Expanded: name + icon, with the trigger on the right (Ramp/Linear).
 * Collapsed: workspace icon; hovering the sidebar swaps it for the expand trigger.
 */
function AppSidebarHeader({
  workspace,
  workspaces,
  settingsGeneralPath,
  settingsMembersPath,
  onSignOut,
}: {
  workspace: WorkspaceSummary | null;
  workspaces: WorkspaceSummary[];
  settingsGeneralPath: string;
  settingsMembersPath: string;
  onSignOut: () => void;
}) {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const fallbackWorkspace = getWorkspaceDefaults();
  const workspaceName = workspace?.name ?? fallbackWorkspace.name;
  const workspaceLogoUrl = workspace?.logoUrl ?? null;
  const workspaceInitial = getSidebarWorkspaceInitial(workspaceName);

  return (
    <SidebarHeader>
      <SidebarMenu>
        <SidebarMenuItem>
          <div className="flex w-full items-center gap-1">
            {collapsed ? (
              <SidebarTrigger className="hidden size-8 group-hover/sidebar:flex" />
            ) : null}
            <div className={cn('min-w-0 flex-1', collapsed && 'group-hover/sidebar:hidden')}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    aria-label={getSidebarWorkspaceMenuLabel(workspaceName)}
                    className="data-[state=open]:bg-sidebar-accent"
                  >
                    <WorkspaceSidebarLogo
                      name={workspaceName}
                      logoUrl={workspaceLogoUrl}
                      initial={workspaceInitial}
                      className="flex aspect-square size-5 shrink-0 items-center justify-center overflow-hidden rounded-md bg-brand text-[10px] font-semibold text-brand-foreground"
                      imageClassName="h-full w-full object-cover"
                    />
                    <span
                      className={cn('truncate font-semibold', sidebarCollapsedTriggerTextClassName)}
                    >
                      {workspaceName}
                    </span>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="right" sideOffset={4} className="w-72">
                  <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                    Workspaces
                  </DropdownMenuLabel>
                  {workspaces.map((item) => (
                    <DropdownMenuItem key={item.id} asChild className="gap-2">
                      {/* A full load, so no in-memory state (Spec runtime, caches) crosses workspaces. */}
                      <a href={getWorkspaceSwitchPath(item)}>
                        <WorkspaceSidebarLogo
                          name={item.name}
                          logoUrl={item.logoUrl}
                          initial={getSidebarWorkspaceInitial(item.name)}
                          className="flex size-7 shrink-0 items-center justify-center overflow-hidden rounded-md bg-brand text-xs font-semibold text-brand-foreground"
                          imageClassName="h-full w-full object-cover"
                        />
                        <span className="min-w-0 truncate font-medium">{item.name}</span>
                        <span className="shrink-0 rounded-md bg-brand-muted px-1.5 py-0.5 text-[11px] font-medium text-brand">
                          {WORKSPACE_ROLE_LABEL[item.role]}
                        </span>
                        {item.slug === workspace?.slug ? (
                          <Check className="ml-auto h-4 w-4" aria-label="Current workspace" />
                        ) : null}
                      </a>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to={settingsGeneralPath}>
                      <Settings />
                      Workspace settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to={settingsMembersPath}>
                      <Users />
                      Invite and manage members
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/signup/workspace">
                      <Plus />
                      Create new workspace
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onSignOut()}>
                    <LogOut />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {!collapsed ? <SidebarTrigger className="shrink-0" /> : null}
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>
  );
}

const WORKSPACE_ROLE_LABEL: Record<WorkspaceSummary['role'], string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
};

function WorkspaceSidebarLogo({
  name,
  logoUrl,
  initial,
  className,
  imageClassName,
}: {
  name: string;
  logoUrl: string | null;
  initial: string;
  className: string;
  imageClassName: string;
}) {
  const alt = `${name} logo`;

  if (logoUrl?.startsWith('/api/v1/')) {
    return (
      <div className={className}>
        <AuthenticatedImage src={logoUrl} alt={alt} className={imageClassName} />
      </div>
    );
  }

  if (logoUrl) {
    return (
      <div className={className}>
        <img src={logoUrl} alt={alt} className={imageClassName} />
      </div>
    );
  }

  return <div className={className}>{initial}</div>;
}

function SettingsSidebarHeader({ appHomePath }: { appHomePath: string }) {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';

  return (
    <SidebarHeader>
      <SidebarMenu>
        <SidebarMenuItem>
          <div className="flex w-full items-center gap-1">
            {collapsed ? (
              <SidebarTrigger className="hidden size-8 group-hover/sidebar:flex" />
            ) : null}
            <div className={cn('min-w-0 flex-1', collapsed && 'group-hover/sidebar:hidden')}>
              <SidebarMenuButton
                asChild
                tooltip={sidebarBackToAppLabel}
                aria-label={sidebarBackToAppLabel}
              >
                <Link to={appHomePath}>
                  <ArrowLeft />
                  <span className={sidebarCollapsedTriggerTextClassName}>Back to app</span>
                </Link>
              </SidebarMenuButton>
            </div>
            {!collapsed ? <SidebarTrigger className="shrink-0" /> : null}
          </div>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarHeader>
  );
}

function AppSidebarContent({
  isActive,
  linkFor,
  homeBadgeCount,
  favorites,
  activeFavoriteId,
  workspaceSlug,
  userId,
  workspaceId,
  onUnfavorite,
  onReorder,
}: {
  isActive: (to: string) => boolean;
  linkFor: (to: `/${string}`) => string;
  homeBadgeCount: number;
  favorites: WorkspaceView[];
  activeFavoriteId: string | null;
  workspaceSlug: string | null;
  userId: string | undefined;
  workspaceId: string | undefined;
  onUnfavorite: (viewId: string) => void;
  onReorder: (viewIds: readonly string[]) => void;
}) {
  const launchState = useSpecLaunchState();
  const showSpec = isSpecSurfaceVisible(launchState);

  return (
    <>
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            {home.map((item) => {
              if ('specOnly' in item && item.specOnly && !showSpec) return null;
              return (
                <SidebarMenuItem key={item.to}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(linkFor(item.to))}
                    tooltip={item.label}
                  >
                    <Link to={linkFor(item.to)}>
                      <item.icon />
                      <span>{item.label}</span>
                      {item.to === '/feed' && homeBadgeCount > 0 ? (
                        <span className="ml-auto inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-brand-muted px-1.5 text-xs font-medium text-brand">
                          {homeBadgeCount}
                        </span>
                      ) : null}
                      {item.to === '/spec' ? <SpecAiMark /> : null}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {showSpec ? (
        <React.Suspense fallback={<SpecSidebarChatsSkeleton />}>
          <LazySpecSidebarChats specPath={linkFor('/spec')} />
        </React.Suspense>
      ) : null}

      <SidebarGroup className="pt-0">
        <SidebarGroupLabel>Data</SidebarGroupLabel>
        <SidebarGroupContent>
          <SidebarMenu>
            {research.map((item) => (
              <SidebarMenuItem key={item.to}>
                <SidebarMenuButton
                  asChild
                  isActive={isActive(linkFor(item.to))}
                  tooltip={item.label}
                >
                  <Link to={linkFor(item.to)}>
                    <item.icon />
                    <span>{item.label}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {workspaceSlug && userId && workspaceId ? (
        <FavoriteViewsSection
          favorites={favorites}
          activeViewId={activeFavoriteId}
          workspaceSlug={workspaceSlug}
          userId={userId}
          workspaceId={workspaceId}
          onUnfavorite={onUnfavorite}
          onReorder={onReorder}
        />
      ) : null}
    </>
  );
}

/** One settings item; one with children is a collapsible group, open while a child is the current page. */
export function SettingsNavEntry({
  item,
  isActive,
  linkFor,
}: {
  item: SettingsNavItem;
  isActive: (to: string) => boolean;
  linkFor: (to: `/${string}`) => string;
}) {
  const childActive = (item.children ?? []).some((child) => isActive(linkFor(child.to)));
  const [open, setOpen] = React.useState(childActive);
  const { state, isMobile } = useSidebar();
  React.useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);
  // Collapsed to icons, a group's pages are hidden, so its icon goes to the first of them.
  if (!item.children || (state === 'collapsed' && !isMobile)) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          isActive={isActive(linkFor(item.to)) || childActive}
          tooltip={item.label}
        >
          <Link to={linkFor(item.to)}>
            <item.icon />
            <span>{item.label}</span>
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }
  // A group's row only opens and closes it, as a collapsible sidebar group does; its pages are the children.
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        tooltip={item.label}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <item.icon />
        <span>{item.label}</span>
        <ChevronRight
          className={cn('ml-auto transition-transform duration-200', open && 'rotate-90')}
        />
      </SidebarMenuButton>
      {item.children ? (
        <>
          {open ? (
            <SidebarMenuSub>
              {item.children.map((child) => (
                <SidebarMenuSubItem key={child.to}>
                  <SidebarMenuSubButton asChild isActive={isActive(linkFor(child.to))}>
                    <Link to={linkFor(child.to)}>
                      <span>{child.label}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          ) : null}
        </>
      ) : null}
    </SidebarMenuItem>
  );
}

function SettingsSidebarContent({
  isActive,
  linkFor,
  capabilities,
}: {
  isActive: (to: string) => boolean;
  linkFor: (to: `/${string}`) => string;
  capabilities: WorkspaceCapabilities | null;
}) {
  // Spec AI is gated on the same surface-visibility switch the app-mode Spec
  // item uses, NOT on canManageSpecTools: production launch state is `off` so
  // the group stays hidden there, while every member of a Spec-enabled
  // workspace can still reach Tools to manage their own personal overrides.
  const launchState = useSpecLaunchState();
  const specVisible = isSpecSurfaceVisible(launchState);
  // Drop groups whose only items are capability-gated away (e.g. Developer →
  // API Keys for members without canManageApiKeys) so we never render a bare
  // section header with nothing under it.
  const groups = filterSettingsNavGroups(settingsNav, capabilities, { specVisible }).filter(
    (group) => group.items.length > 0,
  );
  return (
    <>
      {groups.map((group) => (
        <SidebarGroup key={group.label} className={group.label === 'Personal' ? undefined : 'pt-0'}>
          <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {group.items.map((item) => (
                <SettingsNavEntry key={item.to} item={item} isActive={isActive} linkFor={linkFor} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      ))}
    </>
  );
}
